import { SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TParsedProductInstance } from '@lib/core/products/types';
import { TRetailerLocationStoreType } from '@lib/core/retailers/types';
import { RetailerLocationStoreType } from '@lib/core/retailers/utils/consts';
import { BrowserUrlConsts } from '@lib/core/service/consts';
import { prependBasename, promotionsFilter } from '@lib/core/service/utils';
import { CustomInteraction } from '@lib/tools/customInteraction';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { MixpanelPositionContext } from '@lib/tools/dat/mixpanel/consts';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { useModals } from '@lib/tools/modals/hooks';
import {
  MODALS,
  PROMOTION_BADGE_FIDELITY,
  PROMOTION_BADGE_OTHER,
  PROMOTION_BADGE_PROMOTION,
  PROMOTION_LABEL_SLUG,
} from '@lib/tools/shared/helpers/consts';
import { useAddons } from '@lib/tools/views/hooks';
import { PAGES } from '@lib/tools/views/urls';

import Button from '@components/web/src/atoms/Buttons/Button';
import { PriceRange } from '@components/web/src/atoms/PriceRange/PriceRange';
import { RatingButton } from '@components/web/src/atoms/ProductRating/ProductRating/RatingButton';
import Wishlist from '@components/web/src/atoms/Wishlist/Wishlist';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Image } from '@components/web/src/foundations/Image/Image';
import { Text } from '@components/web/src/foundations/Text/Text';
import {
  IntentGate,
  IntentGateProps,
} from '@components/web/src/organisms/ProductCards/ProductCard/components/IntentGate';
import * as S from '@components/web/src/organisms/ProductCards/ProductCard/styles';
import { PromotionBadges } from '@components/web/src/organisms/PromotionBadges/PromotionBadges';
import NewProductCardSkeleton from '@components/web/src/organisms/Skeleton/NewProductCardSkeleton/NewProductCardSkeleton';
import { SkeletonWrapper } from '@components/web/src/organisms/Skeleton/SkeletonWrapper';
import { COLORS, RETAILER_COLORS } from '@components/web/src/styles';
import { ProductRateModal } from '@components/web/src/templates/Modals/ProductRateModal/ProductRateModal';

interface ProductCardBaseProps {
  parsedProductInstanceData?: TParsedProductInstance;
  storeType?: TRetailerLocationStoreType;
  mixpanelIndex?: number | null;
  mixpanelPositionContext?: MixpanelPositionContext;
  isCardClickDisabled?: boolean;
  isLoading?: boolean;
  isResponsive?: boolean;
  shouldHideWishlist?: boolean;
  shouldShowFeedbackTooltip?: boolean;
  shouldHidePrice?: boolean;
  shouldHidePromotionBadges?: boolean;
  shouldHideCTA?: boolean;
  isActiveCard?: boolean;
}

/** When isSuggestionVariant is true, intentGateProps is required. */
type Props = ProductCardBaseProps &
  (
    | { isSuggestionVariant?: false; intentGateProps?: IntentGateProps }
    | { isSuggestionVariant: true; intentGateProps: IntentGateProps }
  );

export const ProductCard = ({
  parsedProductInstanceData,
  storeType,
  mixpanelIndex,
  mixpanelPositionContext,
  isCardClickDisabled = false,
  isLoading = false,
  // UI
  isSuggestionVariant = false,
  isResponsive = false,
  shouldHideWishlist,
  shouldShowFeedbackTooltip,
  shouldHidePrice = false,
  shouldHidePromotionBadges = false,
  shouldHideCTA = false,
  isActiveCard,
  intentGateProps,
}: Props) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const {
    commonMessages: { promo },
    productCard: { tasted, rated },
  } = localeCommon;

  const { isFeedbackModalOpen, closeFeedbackModal, handleToggleModal } = useModals();
  const { isPriceRangeAddon } = useAddons();

  const {
    productCategory,
    productCharacterName,
    productCharacterTechnicalCaption,
    productName,
    productProducerUrl,
    productId,
    productInstanceId,
    promotions,
    productOriginalPriceAndCurrency,
    productDiscountPriceAndCurrency,
    productDiscountPrice,
    productImage,
    isProductInstanceInWishlist,
    isWishlistProductListLoading,
    productFeedbackStatus,
    productOriginalPrice,
    productRate,
    productRateOptions,
    productPriceCurrencySymbol,
    isProductRateUpdateLoading,
    productProducerName,
    isGameEnabled,
    isUsedInGame,
    productFormat,
    productFormatName,
    productFormatSize,
    productBackgroundImage,
    productTags,
    productDescription,
    handleSelectGameProduct,
    handleUpdateWishlistProductList,
    handleUpdateProductRating,
  } = parsedProductInstanceData || {};

  const isProductDiscountAvailable = !!productDiscountPrice && !!productOriginalPrice;

  const isShowPromoLabel = !!promotionsFilter(promotions, [PROMOTION_LABEL_SLUG]).length;
  const isShowRatingButton = (productFeedbackStatus?.isFinished && !!productRate) || !!productRate;

  const promotionBadges = promotionsFilter(promotions, [
    PROMOTION_BADGE_FIDELITY,
    PROMOTION_BADGE_PROMOTION,
    PROMOTION_BADGE_OTHER,
  ]);

  const characterTitle = `${productCharacterName && `**#${productCharacterName}** - `} ${productCharacterTechnicalCaption}`;
  const productFormatText = `${productFormatName}${productFormatSize && `, ${productFormatSize}`}`;

  const handleAreaClick = (e: SyntheticEvent) => e.stopPropagation();

  const handleCardClick = (e: SyntheticEvent) => {
    handleAreaClick(e);

    const isExternalTarget = storeType === RetailerLocationStoreType.ecommerce;
    if (isGameEnabled) {
      if (isUsedInGame || productRate) return;
      handleSelectGameProduct();
      MixpanelTracker.events.gameProductChanged(parsedProductInstanceData);
      navigate(prependBasename(PAGES.vinhood.action));
    } else if (storeType === RetailerLocationStoreType.main) {
      navigate(prependBasename(PAGES.vinhood.product, { [BrowserUrlConsts.productId]: productInstanceId }), {
        state: { from: pathname + search },
      });

      MixpanelTracker.events.productClick({
        parsedProductInstanceData,
        productIndex: mixpanelIndex,
        productPosition: mixpanelPositionContext,
      });
    } else if (isExternalTarget && productProducerUrl) {
      window.open(productProducerUrl, '_blank');
      MixpanelTracker.events.contactTheProductProducer(
        parsedProductInstanceData,
        mixpanelIndex,
        mixpanelPositionContext,
      );
    }
  };

  return (
    <>
      <SkeletonWrapper showSkeleton={isLoading} skeleton={() => NewProductCardSkeleton({ isResponsive })}>
        <S.ProductCardContainer
          $isCardClickDisabled={isCardClickDisabled}
          $isProductFeedbackFinished={productFeedbackStatus?.isFinished || !!productRate}
          $isResponsive={isResponsive}
          direction="column"
          gap={0}
          isFullHeight={!isSuggestionVariant}
          onClick={handleCardClick}
        >
          <S.ProductImageWrapper
            $productBackgroundImage={productBackgroundImage}
            align="center"
            gap={0}
            justify="center"
          >
            <S.ProductImage alt="product image" src={productImage} />
            {isShowPromoLabel && (
              <S.PromoLabel>
                <Text color={COLORS.base.white} size="body3" text={promo} textTransform="uppercase" weight="semibold" />
              </S.PromoLabel>
            )}
            <S.ProductTags>
              {productTags?.map((tag, idx) => (
                <Image key={idx} alt={`${productId}-${tag.tagName}`} height="32px" src={tag.icon} width="32px" />
              ))}
            </S.ProductTags>
            {!isSuggestionVariant && (
              <S.CTAContainer align="center" direction="column" gap={0} onClick={handleAreaClick}>
                {!shouldHideWishlist && (
                  <Wishlist
                    isItemInWishlist={isProductInstanceInWishlist}
                    isItemListLoading={isWishlistProductListLoading}
                    handleUpdateWishlist={() => {
                      handleUpdateWishlistProductList();

                      MixpanelTracker.events.productBookmark(
                        parsedProductInstanceData,
                        mixpanelIndex,
                        mixpanelPositionContext,
                      );
                    }}
                  />
                )}
                {isShowRatingButton && (
                  <RatingButton
                    parsedProductInstanceData={parsedProductInstanceData}
                    shouldShowFeedbackTooltip={shouldShowFeedbackTooltip}
                  />
                )}
              </S.CTAContainer>
            )}
          </S.ProductImageWrapper>
          <S.ProductContentWrapper direction="column">
            {!isSuggestionVariant && (
              <S.ProducerName
                shouldRenderEmptySelector
                color={COLORS.base.black}
                linesLimit={1}
                size="body1"
                text={productProducerName}
                weight="semibold"
              />
            )}
            <S.ProductName
              color={COLORS.base.black}
              fontType="grace"
              linesLimit={2}
              size="h6"
              text={productName}
              weight="semibold"
            />
            {isSuggestionVariant ? (
              <>
                <S.ProductDescription color={COLORS.base.black} linesLimit={8} size="body3" text={productDescription} />
                <IntentGate
                  handlePostFeedbackDispatched={intentGateProps.handlePostFeedbackDispatched}
                  isActiveCard={isActiveCard}
                  mixpanelIndex={mixpanelIndex}
                  mixpanelPositionContext={mixpanelPositionContext}
                  navigateToProductUrl={intentGateProps.navigateToProductUrl}
                  parsedProductInstanceData={parsedProductInstanceData}
                />
              </>
            ) : (
              <>
                <S.CharacterTitle
                  color={RETAILER_COLORS.secondary['900']}
                  linesLimit={2}
                  size="body3"
                  text={characterTitle}
                  translate="no"
                />
                <S.ProductPriceFormatWrapper isFullWidth align="flex-start" gap={8} justify="space-between">
                  {productFormat && <S.ProductFormat size="body2" text={productFormatText} />}
                  {!shouldHidePrice &&
                    (isPriceRangeAddon ? (
                      <PriceRange
                        currencySymbolValue={productPriceCurrencySymbol}
                        originalPrice={productOriginalPrice}
                        productCategory={productCategory}
                        textSize="h6"
                      />
                    ) : (
                      <Flexbox align="center" gap={8} justify="flex-end">
                        {isProductDiscountAvailable && (
                          <Text
                            color={COLORS.gray[400]}
                            size="h6"
                            text={productOriginalPriceAndCurrency}
                            textDecoration="line-through"
                          />
                        )}
                        {(isProductDiscountAvailable || !!productOriginalPrice) && (
                          <Text
                            color={COLORS.base.black}
                            size="h6"
                            weight="semibold"
                            text={
                              isProductDiscountAvailable
                                ? productDiscountPriceAndCurrency
                                : productOriginalPriceAndCurrency
                            }
                          />
                        )}
                      </Flexbox>
                    ))}
                </S.ProductPriceFormatWrapper>
                {!shouldHidePromotionBadges && !!promotionBadges?.length && (
                  <PromotionBadges promotionsData={promotionBadges} />
                )}
                {!shouldHideCTA && (productFeedbackStatus?.isFinished || productRate) && (
                  <S.FeedbackStatus isFullWidth align="center" justify="center">
                    <Text
                      color={RETAILER_COLORS.product['500']}
                      size="body3"
                      text={productFeedbackStatus?.isFinished ? tasted : rated}
                      weight="semibold"
                    />
                    <svg fill="none" height="18" viewBox="0 0 19 18" width="19">
                      <path
                        d="M15.5 4.5L7.25 12.75L3.5 9"
                        stroke={RETAILER_COLORS.product['500']}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  </S.FeedbackStatus>
                )}
                {!shouldHideCTA && !isUsedInGame && (
                  <CustomInteraction
                    mixpanelIndex={mixpanelIndex}
                    mixpanelPositionContext={mixpanelPositionContext}
                    parsedProductInstanceData={parsedProductInstanceData}
                    renderButton={({ text, onClick, isDisabledButton }) => (
                      <Button
                        isPulseAnimation
                        disabled={isDisabledButton}
                        style={{ marginTop: 'auto', zIndex: 2 }}
                        text={text}
                        onClick={onClick}
                      />
                    )}
                  />
                )}
              </>
            )}
          </S.ProductContentWrapper>
        </S.ProductCardContainer>
      </SkeletonWrapper>

      {isFeedbackModalOpen && parsedProductInstanceData && (
        <ProductRateModal
          handleOpenDiscoveryQuizModal={() => handleToggleModal(MODALS.DISCOVERY_QUIZ_MODAL)}
          handleUpdateProductRating={handleUpdateProductRating}
          hideModal={() => closeFeedbackModal()}
          isLoading={isProductRateUpdateLoading}
          isModalOpen={isFeedbackModalOpen}
          parsedProductInstanceData={parsedProductInstanceData}
          productRateOptions={productRateOptions}
        />
      )}
    </>
  );
};
